import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { map, Observable, startWith, Subject, Subscription, tap } from 'rxjs';

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  standalone: false,
})
export class AutocompleteComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() data: any[] = [];
  @Input() control: any = '';
  @Input() label: string = '';
  @Input() value: string = '';
  @Input() name: string = '';
  @Input() submitted = false;
  @Input() required: boolean = false;
  @Input() placeholder: string = 'Search';
  @Input() filterKeys: any[] = [];
  @Input() forceUppercase = false;
  @Input() commitExactMatchOnBlur = false;
  @Output() emitSelectedValue = new EventEmitter<any>();

  searchText = new Subject<string>();
  results!: Observable<any[]>;
  notFound = false;
  result: any;
  highlightedOption: number = 0;

  /** Input display — kept separate so edit patchValue does not lock typing */
  displayText = '';
  private editing = false;
  private valueChangesSub?: Subscription;
  private searchSub?: Subscription;

  constructor() {
    this.results = this.searchText.pipe(
      startWith(''),
      map((value: string) => this.filter(value)),
      tap((results: string[]) => (this.notFound = results.length === 0))
    );
  }

  ngOnInit(): void {
    this.displayText = this.control?.value ?? '';

    // Parent often patchValue() after child ngOnInit — sync display when not editing
    this.valueChangesSub = this.control?.valueChanges?.subscribe((v: string) => {
      if (!this.editing) {
        this.displayText = v ?? '';
      }
    });

    this.searchSub = this.searchText.subscribe((value) => {
      this.onSearchTextChange(value);
    });
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }

  onFocus(): void {
    this.editing = true;
  }

  onNativeInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.onModelChange(raw);
  }

  onSearchTextChange(value: string): void {
    if (value === null || value === undefined) {
      this.emitSelectedValue.emit('');
      return;
    }
    const str = String(value).trim();
    if (!str) {
      this.emitSelectedValue.emit('');
      return;
    }
    const normalized = this.forceUppercase ? str.toUpperCase() : str;
    const matched = this.data.find(
      (d) =>
        d[this.value]?.toString().toLowerCase() === normalized.toLowerCase()
    );
    this.emitSelectedValue.emit(matched ? matched[this.value] : '');
  }

  onModelChange(raw: string | null | undefined): void {
    this.editing = true;
    if (raw === null || raw === undefined) {
      this.displayText = '';
      this.searchText.next('');
      return;
    }
    // Guard: MDB may surface option objects via its CVA path
    if (typeof raw === 'object') {
      const selected =
        (raw as any)[this.value] ?? (raw as any).value ?? '';
      this.displayText = selected;
      this.searchText.next(selected);
      return;
    }
    const v = this.forceUppercase ? String(raw).toUpperCase() : String(raw);
    this.displayText = v;
    this.searchText.next(v);
  }

  onInputBlur(event: FocusEvent): void {
    if (!this.commitExactMatchOnBlur) {
      this.editing = false;
      return;
    }
    const input = event.target as HTMLInputElement;
    // Defer so (selected) from the panel can run first when the user clicks an option.
    setTimeout(() => {
      const raw = (input?.value ?? '').trim();
      const normalized = this.forceUppercase ? raw.toUpperCase() : raw;
      if (!normalized) {
        this.displayText = '';
        this.searchText.next('');
        this.emitSelectedValue.emit('');
        this.editing = false;
        return;
      }
      const matched = this.data.find(
        (d) =>
          d[this.value]?.toString().toLowerCase() === normalized.toLowerCase()
      );
      if (matched) {
        const canonical = matched[this.value];
        this.displayText = canonical;
        this.searchText.next(canonical);
        this.emitSelectedValue.emit(canonical);
      } else {
        this.displayText = '';
        this.searchText.next('');
        this.emitSelectedValue.emit('');
      }
      this.editing = false;
    }, 0);
  }

  onKeydown(event: KeyboardEvent) {
    if (['Tab', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      if (event.key === 'Tab') {
        this.handleChange({
          option: { value: this.result[this.highlightedOption] },
        });
      }

      if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        if (event.key === 'ArrowUp' && this.highlightedOption > 0) {
          this.highlightedOption--;
        } else if (
          event.key === 'ArrowDown' &&
          this.highlightedOption < this.result.length - 1
        ) {
          this.highlightedOption++;
        }
      }
    } else {
      this.highlightedOption = 0;
    }
  }

  getValue(option: any) {
    return option[this.value];
  }

  getOption(option: any) {
    return option[this.name];
  }

  filter(value: any): any[] {
    const filterValue =
      typeof value === 'string'
        ? value?.toLowerCase()
        : value.value.toLowerCase();
    this.result = this.data.filter((item: any) =>
      this.filterKeys.some((key) => {
        return item[key]?.toLowerCase().includes(filterValue);
      })
    );
    return this.result;
  }

  /**
   * Used by MDB on select (option object) and if writeValue runs (model string).
   * Must handle both — returning undefined for strings clears the input.
   */
  displayValue = (option: any): string => {
    if (option == null || option === '') {
      return '';
    }
    if (typeof option === 'string' || typeof option === 'number') {
      return String(option);
    }
    return option[this.value] ?? option['value'] ?? '';
  };

  handleChange({ option }: { option: { value: any } }): void {
    const raw = option?.value;
    const selected =
      raw != null && typeof raw === 'object' ? raw[this.value] : raw;
    this.displayText = selected ?? '';
    this.searchText.next(this.displayText);
    this.emitSelectedValue.emit(this.displayText);
    this.editing = false;
  }
}
